import { Add, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Collapse,
  Grid2Props,
  IconButton,
  Typography,
  darken,
  styled,
} from '@mui/material';
import React, { useCallback, useMemo, useState } from 'react';
import { useDemoForm } from '../../context/DemoFormContext';
import useChipDialog from '../../hooks/dialog/useChipDialog';
import ProxyConceptChip from '../ChipSwapper/ProxyConceptChip';
import { SummaryItemGrid } from './components';

const HeaderBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  boxSizing: 'border-box',
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
}));

const ChipsDisplayBoxWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  boxSizing: 'border-box',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  },
}));

const ChipsDisplayBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  boxSizing: 'border-box',
  overflow: 'auto',
  [theme.breakpoints.up('md')]: {
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    gap: theme.spacing(2),
  },
}));

export interface ConceptsBoxProps extends Grid2Props {
  isExclusion: boolean;
}

const ConceptsBox: React.FC<ConceptsBoxProps> = ({ isExclusion, ...props }) => {
  const { excludedConcepts, includedConcepts } = useDemoForm();
  const [expanded, setExpanded] = useState(true);
  const openDialog = useChipDialog({ isExclusion });

  const myConcepts = useMemo(
    () => (isExclusion ? excludedConcepts : includedConcepts) || [],
    [isExclusion, excludedConcepts, includedConcepts],
  );
  const count = myConcepts.length;

  const handleClick = useCallback(() => {
    openDialog();
  }, [openDialog]);

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  return (
    <SummaryItemGrid
      flexDirection={'column'}
      id="project-performance-summary-item-excluded-concepts"
      className="project-peformance-summary-item project-performance-summary-item-excluded-concepts exclusions-box"
      justifyContent={'flex-start'}
      alignItems={'flex-start'}
      sx={{
        px: { xs: 1.5, md: 4 },
        py: { xs: 1, md: 2 },
        // Let the card hug the header when collapsed instead of leaving a
        // 100px empty band beneath it.
        minHeight: expanded ? '100px' : 'auto',
      }}
      maxHeight={'200px'}
      xs={12}
      md={6}
      {...props}
    >
      <HeaderBox className="concepts-header-box" gap={1}>
        <Box
          onClick={toggleExpanded}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-controls={`concepts-body-${isExclusion ? 'exclusion' : 'inclusion'}`}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleExpanded();
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            flex: 1,
            minWidth: 0,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          <IconButton
            size="small"
            aria-label={expanded ? 'Collapse concepts' : 'Expand concepts'}
            // Visual indicator only — the parent handles the click. Keep it
            // non-interactive so the click bubbles to the toggle and the
            // button doesn't trap keyboard focus separately.
            tabIndex={-1}
            disableRipple
            sx={{
              p: 0.25,
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
            }}
          >
            <ExpandMore fontSize="small" />
          </IconButton>
          <Typography
            color="inherit"
            fontWeight="bold"
            className="concepts-title"
            sx={(theme) => ({
              fontSize: '0.95rem',
              lineHeight: 1.2,
              [theme.breakpoints.up('md')]: {
                fontSize: theme.typography.h4.fontSize,
                lineHeight: theme.typography.h4.lineHeight,
              },
            })}
          >
            {isExclusion ? 'Excluded Concepts' : 'Included Concepts'}
            {!expanded && count > 0 ? ` (${count})` : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="inherit"
          size="small"
          sx={(theme) => ({
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            fontSize: '0.7rem',
            py: 0.25,
            px: 1,
            minWidth: 0,
            '&:hover': {
              backgroundColor: `${darken(theme.palette.common.white, 0.1)}`,
            },
            '&:active': {
              backgroundColor: `${darken(theme.palette.common.white, 0.2)}`,
            },
            [theme.breakpoints.up('md')]: {
              fontSize: undefined,
              py: undefined,
              px: undefined,
            },
          })}
          onClick={handleClick}
          className="manage-concepts-button"
        >
          <Add fontSize="small" className="add-icon" /> Add More
        </Button>
      </HeaderBox>
      <Collapse
        in={expanded}
        id={`concepts-body-${isExclusion ? 'exclusion' : 'inclusion'}`}
        sx={{ width: '100%' }}
      >
        <ChipsDisplayBoxWrapper>
          <ChipsDisplayBox className="chips-display-box">
            {myConcepts.map((conceptData) => (
              <ProxyConceptChip
                className={`proxy-concept-chip${isExclusion ? ' exclusion' : ' inclusion'}`}
                color={isExclusion ? 'error' : 'secondary'}
                direction={isExclusion ? 'left' : 'right'}
                ignoreFilters={true}
                hideFilters={true}
                key={conceptData}
                id={conceptData}
                aria-label={`Concept: ${conceptData}`} // Accessibility
              />
            ))}
          </ChipsDisplayBox>
        </ChipsDisplayBoxWrapper>
      </Collapse>
    </SummaryItemGrid>
  );
};

export default ConceptsBox;
